import { Tabs, usePathname } from "expo-router";
import { Platform, View } from "react-native";
import { HapticTab } from "@/components/HapticTab";
import TabBarBackground from "@/components/ui/TabBarBackground";
import { useTheme } from "@/hooks/useTheme";
import { ThemedText } from "@/components/ThemedText";
import { MaterialIcons } from "@expo/vector-icons";
import { useEffect, useState } from "react";

const STEPS = ["Eligibility", "Info", "Physical", "Additional", "Review"];

function ProgressIndicator() {
  const { colors } = useTheme();
  const [currentStep, setCurrentStep] = useState(0);
  const pathname = usePathname();

  useEffect(() => {
    // Map pathname to step index
    if (pathname.endsWith("/apply") || pathname.includes("index")) setCurrentStep(0);
    else if (pathname.includes("info")) setCurrentStep(1);
    else if (pathname.includes("physical")) setCurrentStep(2);
    else if (pathname.includes("additional")) setCurrentStep(3);
    else if (pathname.includes("review")) setCurrentStep(4);
    // ineligible is a dead-end, don't update progress
  }, [pathname]);

  return (
    <View
      style={{
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        paddingHorizontal: 16,
        paddingVertical: 12,
        backgroundColor: colors.card,
        borderBottomWidth: 1,
        borderBottomColor: colors.border,
      }}
    >
      {STEPS.map((step, index) => (
        <View
          key={step}
          style={{
            flex: index < STEPS.length - 1 ? 1 : undefined,
            alignItems: "center",
            flexDirection: "row",
          }}
        >
          <View
            style={{
              width: 24,
              height: 24,
              borderRadius: 12,
              backgroundColor: index <= currentStep ? colors.primary : colors.border,
              alignItems: "center",
              justifyContent: "center",
              marginRight: 4,
            }}
          >
            {index < currentStep ? (
              <MaterialIcons name="check" size={14} color="white" />
            ) : (
              <ThemedText
                style={{
                  fontSize: 11,
                  fontWeight: "600",
                  color: index <= currentStep ? "white" : colors.textSecondary,
                }}
              >
                {index + 1}
              </ThemedText>
            )}
          </View>
          <ThemedText
            style={{
              fontSize: 10,
              color: index <= currentStep ? colors.primary : colors.textSecondary,
              fontWeight: index === currentStep ? "600" : "400",
              display: Platform.select({ web: "flex", default: index === currentStep ? "flex" : "none" }),
            }}
            numberOfLines={1}
          >
            {step}
          </ThemedText>
          {index < STEPS.length - 1 && (
            <View
              style={{
                flex: 1,
                height: 2,
                backgroundColor: index < currentStep ? colors.primary : colors.border,
                marginHorizontal: 4,
              }}
            />
          )}
        </View>
      ))}
    </View>
  );
}

export default function WizardLayout() {
  const { colors } = useTheme();

  return (
    <View style={{ flex: 1 }}>
      <ProgressIndicator />
      <Tabs
        layout={({ children }) => (
          <View
            style={{
              flex: 1,
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <View
              style={{
                flex: 1,
                width: "100%",
                maxWidth: 800,
              }}
            >
              {children}
            </View>
          </View>
        )}
        screenOptions={{
          animation: "shift",
          tabBarHideOnKeyboard: true,
          sceneStyle: {
            backgroundColor: colors.backgroundSecondary,
            borderColor: colors.border,
          },
          tabBarLabelPosition: Platform.select({
            default: "below-icon",
          }),
          tabBarLabelStyle: Platform.select({
            default: { color: colors.text, fontSize: 12 },
          }),
          tabBarActiveBackgroundColor: colors.primary,
          tabBarActiveTintColor: "white",
          headerShown: false,
          headerShadowVisible: false,
          tabBarBadgeStyle: { maxWidth: 43, top: -6, right: -6 },
          tabBarButton: HapticTab,
          tabBarBackground: TabBarBackground,
          tabBarStyle: { display: "none" },
        }}
      >
        <Tabs.Screen
          name="index"
          options={{
            title: "Eligibility",
            animation: "shift",
            tabBarIcon: ({ color }: any) => <MaterialIcons name="check-circle" size={24} color={color} />,
          }}
        />
        <Tabs.Screen
          name="ineligible"
          options={{
            title: "Ineligible",
            animation: "shift",
            href: null, // Hide from tab bar
            tabBarIcon: ({ color }: any) => <MaterialIcons name="block" size={24} color={color} />,
          }}
        />
        <Tabs.Screen
          name="info"
          options={{
            title: "Info",
            animation: "shift",
            tabBarIcon: ({ color }: any) => <MaterialIcons name="person" size={24} color={color} />,
          }}
        />
        <Tabs.Screen
          name="physical"
          options={{
            title: "Physical",
            animation: "shift",
            tabBarIcon: ({ color }: any) => <MaterialIcons name="fitness-center" size={24} color={color} />,
          }}
        />
        <Tabs.Screen
          name="additional"
          options={{
            title: "Additional",
            animation: "shift",
            tabBarIcon: ({ color }: any) => <MaterialIcons name="help" size={24} color={color} />,
          }}
        />
        <Tabs.Screen
          name="review"
          options={{
            title: "Review",
            animation: "shift",
            tabBarIcon: ({ color }: any) => <MaterialIcons name="done-all" size={24} color={color} />,
          }}
        />
      </Tabs>
    </View>
  );
}
